import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { AddressDTO, API, CompanyFullDTO } from '../../../services/api/api';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-company-edit',
  templateUrl: './company-edit.component.html',
  styleUrls: ['./company-edit.component.scss'],
  standalone: false,
})
export class CompanyEditComponent implements OnInit {
  @Input() isInIntegratedContext;
  @Output() companyEmitter: EventEmitter<CompanyFullDTO> = new EventEmitter<CompanyFullDTO>();

  company: CompanyFullDTO;
  companyId: number;
  loading: boolean;
  wizardState: string;
  point: any;
  form: UntypedFormGroup;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.point = null;
    this.company = undefined;
    this.companyId = null;
    this.loading = true;
    this.wizardState = 'Map';
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      UniqueIdentifier: new UntypedFormControl(''),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get UniqueIdentifier() {
    return this.form.get('UniqueIdentifier');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.companyId = params.companyID;
      this.refreshFromServer();
    });

    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.company.name = value;
    });
    this.UniqueIdentifier.valueChanges.subscribe(value => {
      this.company.identifier = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.company.name);
    this.Name.markAsTouched();
    this.UniqueIdentifier.setValue(this.company.identifier);
    this.UniqueIdentifier.markAsTouched();
    if (this.company?.address?.position?.coordinates) {
      this.point = {
        x: this.company?.address?.position?.coordinates[0],
        y: this.company?.address?.position?.coordinates[1],
        zoom: 16,
      };
    }
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  createCompany(e: CompanyFullDTO) {
    this.companyEmitter.emit(e);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.companyId) {
      this.company = new CompanyFullDTO();
      const address: AddressDTO = new AddressDTO();
      address.country = 'DE';
      this.company.address = address;
      this.loading = false;
    } else {
      this.api
        .shared_Company_retrieveCompanyById(this.companyId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (company: CompanyFullDTO) => {
            this.company = company;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateCompany() {
    this.loading = true;
    if (!this.company.address.position.coordinates[0] || !this.company.address.position.coordinates[1]) {
      this.translate.get(marker('lang.companyEdit.areyousure')).subscribe(title => {
        this.translate.get(marker('lang.companyEdit.areyousureQuestion')).subscribe(message => {
          this.translate.get(marker('lang.globals.yes')).subscribe(yes => {
            this.translate.get(marker('lang.companyEdit.areyousure.no')).subscribe(no => {
              this.modal
                .confirm({
                  title,
                  message,
                  yes,
                  no,
                })
                .then((result: boolean) => {
                  if (result) {
                    this.updateCompanyCall();
                  }
                })
                .catch(() => {});
            });
          });
        });
      });
    } else {
      this.updateCompanyCall();
    }
  }

  updateCompanyCall() {
    if (!this.company.id) {
      this.api
        .shared_Company_createCompany(this.company)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (companyData: CompanyFullDTO) => {
            this.company = companyData;
            if (!this.isInIntegratedContext) {
              this.util.returnToNavigation(this.util.RoutingID.companies);
            } else {
              this.createCompany(companyData);
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .shared_Company_editCompany(this.company)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (companyData: CompanyFullDTO) => {
            this.company = companyData;
            this.util.returnToNavigation(this.util.RoutingID.companies);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  addressUpdate($event: AddressDTO) {
    this.company.address = $event;
  }
}
